// eslint-disable-next-line jira/filename-case
import React, { useCallback, useMemo } from 'react';
import { useJiraInvitePeopleModalBound } from '@jira/project-settings__invite-people-modal-context/src/JiraInvitePeopleModalContextProvider.tsx';
import { projectTypeToSubProduct } from '@jira/issue__invite-people/src/useInvitePeopleFlags.tsx';
// eslint-disable-next-line jira/restricted/unsafe-global-state-stores
import { useProjectSidebarNavigation } from '@jira/navigation-apps__sidebar-common/src/controllers/common/project-sidebar-navigation-resource/index.tsx';
import { useOrgId } from '@jira/router-resources__navigation-organization-id/src/useOrgId.tsx';
import { useCurrentUser } from '@jira/platform__services-user-current/src/main.tsx';
import { useFlagsService } from '@atlassian/jira-flags';
import ErrorFlag from '@atlassian/jira-flags/src/common/ui/components/error-flag';
import SuccessFlag from '@atlassian/jira-flags/src/common/ui/components/success-flag';
import WarningFlag from '@atlassian/jira-flags/src/common/ui/components/warning-flag';

import { useAppEditions } from '@atlassian/jira-tenant-context-controller/src/components/app-editions/index.tsx';
import type { Flag } from '@atlassian/people-teams/flag';
import type { EnablementTouchpoint } from '@atlassian/expansion-services/services';
import { fg } from '@atlassian/jira-feature-gating';

export const useInvitePeopleModal = (touchpoint?: EnablementTouchpoint): (() => void) => {
	const { showFlag } = useFlagsService();
	const { data: projectSidebarNavigationData } = useProjectSidebarNavigation();
	const { data: orgId } = useOrgId();
	const { software } = useAppEditions();

	const { data: currentUser } = useCurrentUser();
	// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
	let continueUrl = window.location.href;

	if (currentUser?.user?.accountId) {
		const url = new URL(continueUrl);
		url.searchParams.append('inviterId', currentUser.user.accountId);
		continueUrl = url.toString();
	}

	const subProduct = projectTypeToSubProduct(projectSidebarNavigationData?.projectType);

	const gatedTouchpoint = fg('groot-905-spaces-touchpoint-prop') ? touchpoint : undefined;

	const addFlag = useCallback(
		({ appearance, ...flagProps }: Flag) => {
			const Flag = getFlagComponent(appearance);
			showFlag((serviceProps) => (
				<Flag
					{...flagProps}
					{...serviceProps}
					messageId={`navigation-apps-sidebar-nav4-sidebars-content-teams.ui.flag.showFlag.${appearance}.${flagProps.id}`}
					messageType="transactional"
				/>
			));
		},
		[showFlag],
	);

	const hookProps = useMemo(
		() => ({
			continueUrl,
			enableThirdParty: true,
			displayInfiSpotlight: true,
			orgId,
			showFlag: addFlag,
			source: 'peopleMenu',
			subProduct,
			applicationEdition: software,
			navigation: { directoryContextEntryPoint: '/jira' },
			touchpoint: gatedTouchpoint,
		}),
		[continueUrl, orgId, addFlag, subProduct, software, gatedTouchpoint],
	);

	const { openModal: invitePeopleModalTrigger } = useJiraInvitePeopleModalBound(hookProps);

	return invitePeopleModalTrigger;
};

const getFlagComponent = (appearance: Flag['appearance']) => {
	switch (appearance) {
		case 'warning':
			return WarningFlag;
		case 'error':
			return ErrorFlag;
		case 'success':
		case 'normal':
		default:
			return SuccessFlag;
	}
};
